import type { ResponseStatusInfo } from '../../types';
import type { Tone } from '../../ui/Badge';

/** Labels for the statuses the backend lists (GET /reference/response-statuses); the server's descriptions are shown beside them. */
export const STATUS_LABEL: Record<string, string> = {
  started: 'In survey',
  complete: 'Complete',
  screenout: 'Screen-out',
  quotaFull: 'Quota full',
  timedOut: 'Timed out',
  qualityTerminate: 'Quality terminate',
  possibleDuplicate: 'Possible duplicate',
};

export const STATUS_TONE: Record<string, Tone> = {
  started: 'brand',
  complete: 'ok',
  screenout: 'neutral',
  quotaFull: 'warn',
  timedOut: 'warn',
  qualityTerminate: 'danger',
  possibleDuplicate: 'danger',
};

/** Final statuses: the only ones reconciliation can set (never `started`). */
export const FINAL_STATUSES = ['complete', 'screenout', 'quotaFull', 'timedOut', 'qualityTerminate', 'possibleDuplicate'] as const;

/** Readable label for a status key; an unknown key (a status the backend adds later) is shown as sent. */
export const statusLabel = (key: string | null | undefined) => (key ? STATUS_LABEL[key] || key : '—');

/** The status key of a reference entry (the message does not name the property, so accept the likely ones). */
export const statusKeyOf = (info: ResponseStatusInfo | string): string => (typeof info === 'string' ? info : String(info.status || info.key || ''));

/** "2 complete, 1 screen-out" for a list of statuses. */
export function describeCounts(statuses: string[]): string {
  const counts = new Map<string, number>();
  statuses.forEach((s) => counts.set(s, (counts.get(s) || 0) + 1));
  const order = Object.keys(STATUS_LABEL);
  const rank = (s: string) => (order.includes(s) ? order.indexOf(s) : order.length);
  return [...counts.entries()].sort((a, b) => rank(a[0]) - rank(b[0])).map(([s, n]) => `${n} ${statusLabel(s).toLowerCase()}`).join(', ');
}

/** Date and time for a session timestamp; '—' when missing, the raw text when it cannot be read. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
